import React, { useState, useEffect, useRef } from "react";
import ReactGA from "react-ga4";
import team from "./part_of_the_team.jpg";
import { useLocation } from "react-router-dom";
import {
    BrowserRouter as Router,
    Routes,
    Route,
    Link,
    useNavigate,
} from "react-router-dom";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
    faPlay,
    faPause,
    faPodcast,
    faMusic,
    faRss,
} from "@fortawesome/free-solid-svg-icons";
import {
    faApple,
    faSpotify,
    faYoutube,
    faXTwitter,
    faFacebook,
} from "@fortawesome/free-brands-svg-icons";
import "./App.css";
import EpisodePage from "./EpisodePage";
import EpisodesPage from "./EpisodesPage";

const RSS_FEED_URL = "https://anchor.fm/s/2e40168/podcast/rss";

function HomePage() {
    const [episodes, setEpisodes] = useState([]);
    const [currentEpisode, setCurrentEpisode] = useState(null);
    const [isPlaying, setIsPlaying] = useState(false);
    const [progress, setProgress] = useState(0);
    const audioRef = useRef(null);
    const navigate = useNavigate();

    useEffect(() => {
        const fetchRSSFeed = async () => {
            try {
                const response = await fetch(RSS_FEED_URL);
                const text = await response.text();
                const parser = new DOMParser();
                const xml = parser.parseFromString(text, "application/xml");
                const items = xml.querySelectorAll("item");
                const allEpisodes = Array.from(items).map((item) => {
                    const descriptionNode = item.querySelector(
                        "itunes\\:summary, description"
                    );
                    const decoder = new DOMParser().parseFromString(
                        descriptionNode?.textContent || "",
                        "text/html"
                    );
                    const descriptionText =
                        decoder.body.textContent.replace(/\n/g, " ").trim() ||
                        "No description available";
                    return {
                        title: item
                            .querySelector("title")
                            .textContent.replace("<![CDATA[", "")
                            .replace("]]>", ""),
                        image:
                            item
                                .querySelector("itunes\\:image, image")
                                ?.getAttribute("href") ||
                            "https://via.placeholder.com/300x200",
                        description: descriptionText,
                        pubDate: new Date(
                            item.querySelector("pubDate").textContent
                        ).toDateString(),
                        audio:
                            item
                                .querySelector("enclosure")
                                ?.getAttribute("url") || "",
                    };
                });
                setEpisodes(allEpisodes);
                setCurrentEpisode(allEpisodes[0]);
            } catch (error) {
                console.error("Error fetching RSS feed:", error);
            }
        };
        fetchRSSFeed();
    }, []);

    const togglePlay = () => {
        ReactGA.event({
            category: "Playback",
            action: "Play Episode",
            label: currentEpisode.title,
        });
        if (!audioRef.current) return;
        if (isPlaying) {
            audioRef.current.pause();
        } else {
            audioRef.current.play();
        }
        setIsPlaying(!isPlaying);
    };
    const [currentTime, setCurrentTime] = useState(0);
    const [duration, setDuration] = useState(0);
    const handleTimeUpdate = () => {
        const audio = audioRef.current;
        if (audio) {
            const progressPercent = (audio.currentTime / audio.duration) * 100;
            setProgress(progressPercent);
            setCurrentTime(audioRef.current.currentTime);
        }
    };
    const handleLoadedMetadata = () => {
        if (audioRef.current) {
            setDuration(audioRef.current.duration);
        }
    };

    // Format time to MM:SS
    const formatTime = (time) => {
        const minutes = Math.floor(time / 60);
        const seconds = Math.floor(time % 60);
        return `${minutes}:${seconds < 10 ? "0" : ""}${seconds}`;
    };
    const handleProgressClick = (e) => {
        const audio = audioRef.current;
        if (!audio) return;
        const bar = e.currentTarget;
        const clickPosition = e.nativeEvent.offsetX;
        const barWidth = bar.clientWidth;
        const newTime = (clickPosition / barWidth) * audio.duration;
        audio.currentTime = newTime;
    };

    return (
        <div className="App">
            <div className="header-wrapper">
                <header>
                    <div className="container header-container">
                        <div className="logo"></div>
                        <nav>
                            <ul>
                                <li>
                                    <Link to="/episodes">Episodes</Link>
                                </li>
                                <li>
                                    <a href="#about">About</a>
                                </li>
                                <li>
                                    <a href="#subscribe">Subscribe</a>
                                </li>
                                <li>
                                    <a href="#contact">Contact</a>
                                </li>
                            </ul>
                        </nav>
                    </div>
                </header>

                {currentEpisode && (
                    <section className="hero">
                        <div className="container hero-content">
                            <img
                                src={currentEpisode.image}
                                alt={currentEpisode.title}
                            />
                            <div className="hero-left">
                                <h1>{currentEpisode.title}</h1>
                                <p className="pub-date">
                                    {currentEpisode.pubDate}
                                </p>
                                <p>{currentEpisode.description}</p>

                                <div className="audio-player">
                                    <button
                                        className="play-button"
                                        onClick={togglePlay}
                                    >
                                        <FontAwesomeIcon
                                            icon={isPlaying ? faPause : faPlay}
                                        />
                                    </button>
                                    <div
                                        className="progress-bar"
                                        onClick={handleProgressClick}
                                    >
                                        <div
                                            className="progress"
                                            style={{ width: `${progress}%` }}
                                        ></div>
                                    </div>
                                </div>
                                <div className="audio-time">
                                    <small>
                                        {formatTime(currentTime)} /{" "}
                                        {formatTime(duration)}
                                    </small>
                                </div>
                                <audio
                                    ref={audioRef}
                                    src={currentEpisode.audio}
                                    onTimeUpdate={handleTimeUpdate}
                                    onLoadedMetadata={handleLoadedMetadata}
                                />
                            </div>
                        </div>
                    </section>
                )}
            </div>
            <main>
                <section id="episodes" className="episodes">
                    <div className="container">
                        <h2>Recent Episodes</h2>
                        <div className="episode-grid">
                            {episodes.length > 1 ? (
                                episodes.slice(1, 4).map((episode, index) => (
                                    <div className="episode" key={index}>
                                        <img
                                            src={episode.image}
                                            alt={episode.title}
                                        />
                                        <h3>{episode.title}</h3>
                                        <p>{episode.description}</p>
                                        <p className="pub-date">
                                            Published: {episode.pubDate}
                                        </p>
                                        <button
                                            className="btn"
                                            onClick={() =>
                                                navigate(
                                                    `/episode/${index + 1}`,
                                                    {
                                                        state: {
                                                            episode,
                                                        },
                                                    }
                                                )
                                            }
                                        >
                                            Listen
                                        </button>
                                    </div>
                                ))
                            ) : (
                                <p>Loading episodes...</p>
                            )}
                        </div>
                    </div>
                </section>

                <section id="about" className="about">
                    <div className="container about-content">
                        <div className="about-left">
                            <img
                                src={team}
                                alt="Most but not all of the team..."
                            />
                        </div>
                        <div className="about-right">
                            <p>
                                Live From The Basement is on a quest to shine a
                                light on the musical and cultural background
                                behind hip-hop.
                            </p>
                            <p>
                                We have been producing our podcast for over 13
                                years independently sharing a multi-textural
                                show focusing on several aspects of hip-hop
                                culture and discussion.
                            </p>
                            <p>
                                With an ever increasing passion and dedication,
                                Live From The Basement still aims to feature
                                thought-provoking, fun and insightful content
                                bridging the gaps between music, culture and
                                lifestyle.
                            </p>
                        </div>
                    </div>
                </section>
                <section id="subscribe" className="subscribe">
                    <div className="container subscribe-content">
                        <h2>Subscribe to Our Podcast</h2>
                        <div className="subscribe-links">
                            <a
                                href="https://podcasts.apple.com/us/podcast/live-from-the-basement/id755399888"
                                target="_blank"
                                rel="noopener noreferrer"
                            >
                                <FontAwesomeIcon icon={faApple} /> iTunes
                            </a>
                            <a
                                href="https://open.spotify.com/show/4uKnULQldxPr0YrHaHtdZ2"
                                target="_blank"
                                rel="noopener noreferrer"
                            >
                                <FontAwesomeIcon icon={faSpotify} /> Spotify
                            </a>
                            <a
                                href="https://pca.st/5A5G"
                                target="_blank"
                                rel="noopener noreferrer"
                            >
                                <FontAwesomeIcon icon={faPodcast} /> Pocket
                                Casts
                            </a>
                            <a
                                href="https://castbox.fm/channel/id1209005?country=us"
                                target="_blank"
                                rel="noopener noreferrer"
                            >
                                <FontAwesomeIcon icon={faMusic} /> Castbox
                            </a>
                            <a
                                href="https://anchor.fm/s/2e40168/podcast/rss"
                                target="_blank"
                                rel="noopener noreferrer"
                            >
                                <FontAwesomeIcon icon={faRss} /> RSS Feed
                            </a>
                        </div>
                    </div>
                </section>
            </main>

            <footer className="footer">
                <div className="container">
                    <p>
                        &copy; {new Date().getFullYear()} Live From The Basement
                        Podcast. All rights reserved.
                    </p>
                    <div className="social-links">
                        <a
                            href="https://www.youtube.com/livethebasement"
                            target="_blank"
                            rel="noopener noreferrer"
                        >
                            <FontAwesomeIcon icon={faYoutube} /> YouTube
                        </a>
                        <a
                            href="https://x.com/livethebasement"
                            target="_blank"
                            rel="noopener noreferrer"
                        >
                            <FontAwesomeIcon icon={faXTwitter} /> X (Twitter)
                        </a>
                        <a
                            href="https://fb.com/livethebasement"
                            target="_blank"
                            rel="noopener noreferrer"
                        >
                            <FontAwesomeIcon icon={faFacebook} /> Facebook
                        </a>
                    </div>
                </div>
            </footer>
        </div>
    );
}
const GA_MEASUREMENT_ID = "G-D95ZYQVW1K"; // Replace with your GA ID

// PageTracker Component: Tracks Route Changes
function PageTracker() {
    const location = useLocation();

    useEffect(() => {
        ReactGA.send({ hitType: "pageview", page: location.pathname });
    }, [location]);

    return null;
}

function App() {
    useEffect(() => {
        ReactGA.initialize(GA_MEASUREMENT_ID); // Replace with your Measurement ID
        ReactGA.send("pageview"); // Sends initial pageview
    }, []);

    return (
        <Router>
            <PageTracker />
            <Routes>
                <Route path="/" element={<HomePage />} />
                <Route path="/episodes" element={<EpisodesPage />} />
                <Route path="/episode/:id" element={<EpisodePage />} />
            </Routes>
        </Router>
    );
}

export default App;
